import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Receipt, Plus, ArrowRight, ShieldCheck, FileText, Lock } from "@phosphor-icons/react";
import { TaxShell, StatusBadge, cad, errToast, Disclaimer } from "@/components/tax/shared";

function ReturnRow({ r, onOpen }) {
  return (
    <button onClick={onOpen} data-testid={`tax-return-row-${r.tax_year}`}
      className="w-full tax-card p-5 flex flex-wrap items-center gap-4 text-left hover:border-red-300 hover:-translate-y-0.5 transition-[transform,border-color] duration-200">
      <div className="h-12 w-12 rounded-xl bg-red-50 text-red-600 flex items-center justify-center font-black">{String(r.tax_year).slice(2)}</div>
      <div className="flex-1 min-w-[180px]">
        <p className="font-bold text-slate-900">{r.tax_year} Personal Tax Return (T1 · Ontario)</p>
        <p className="text-xs text-slate-500 mt-0.5">
          {r.preparer ? `Preparer: ${r.preparer.name}` : "Not yet shared with a preparer"} · Updated {r.updated_at ? new Date(r.updated_at).toLocaleDateString() : "—"}
        </p>
      </div>
      {r.open_requests > 0 && <span className="text-xs font-semibold text-red-600">{r.open_requests} request(s)</span>}
      <div className="text-right">
        <p className="text-[11px] uppercase tracking-wider text-slate-500">{r.balance_owing > 0 ? "Est. balance owing" : "Est. refund"}</p>
        <p className="font-bold text-slate-900">{r.refund == null ? "—" : cad(r.balance_owing > 0 ? r.balance_owing : r.refund)}</p>
      </div>
      <StatusBadge status={r.status} testId={`tax-return-status-${r.tax_year}`} />
      <ArrowRight className="h-5 w-5 text-slate-400" />
    </button>
  );
}

function StartReturn({ years, existing }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const free = years.filter((y) => !existing.includes(y));
  const [year, setYear] = useState(free[0]);
  const create = useMutation({
    mutationFn: async () => (await api.post("/tax/returns", { tax_year: Number(year) })).data,
    onSuccess: (d) => { qc.invalidateQueries({ queryKey: ["tax-returns"] }); nav(`/tax/${d.id}`); },
    onError: errToast,
  });
  if (!free.length) return <p className="text-sm text-slate-500" data-testid="tax-no-years">You've started a return for every supported tax year.</p>;
  return (
    <div className="flex flex-wrap items-end gap-3">
      <div>
        <label htmlFor="tax-year-select" className="text-xs font-semibold text-slate-600 block mb-1.5">Tax year</label>
        <select id="tax-year-select" data-testid="tax-year-select" value={year} onChange={(e) => setYear(e.target.value)}
          className="h-10 rounded-md border border-input px-3 text-sm min-w-[140px]">
          {free.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>
      <Button data-testid="tax-start-return-btn" onClick={() => create.mutate()} disabled={create.isPending}
        className="bg-red-600 hover:bg-red-700 text-white rounded-full px-6">
        <Plus className="h-4 w-4 mr-1.5" weight="bold" /> {create.isPending ? "Starting…" : "Start a new tax return"}
      </Button>
    </div>
  );
}

const STEPS = [
  { icon: FileText, t: "Upload slips", d: "T4, T4A, T5, RRSP, tuition, donations, medical — we read them with AI and you verify every value." },
  { icon: Receipt, t: "Answer & review", d: "Guided questions plus your MyPayAlert income (no double counting). See your estimated refund or balance." },
  { icon: ShieldCheck, t: "Authorize & send", d: "Sign consent and the T183 authorization. An approved tax preparer reviews and files." },
];

export default function TaxCentre() {
  const nav = useNavigate();
  const { data: meta } = useQuery({ queryKey: ["tax-meta"], queryFn: async () => (await api.get("/tax/meta")).data });
  const { data: returns = [], isLoading } = useQuery({ queryKey: ["tax-returns"], queryFn: async () => (await api.get("/tax/returns")).data });

  return (
    <TaxShell testId="tax-centre-page">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-red-600">Tax Centre</p>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight text-slate-900 mt-1">Your Canadian tax return</h1>
          <p className="text-slate-600 mt-2 max-w-xl text-sm md:text-base">Prepare your T1 with federal and Ontario rules for each tax year, then send it securely to an approved tax preparer for review and filing.</p>
        </div>
        <span className="inline-flex items-center gap-1.5 text-xs text-slate-600 bg-white border border-slate-200 rounded-full px-3 py-1.5">
          <Lock className="h-3.5 w-3.5 text-red-600" weight="bold" /> AES-256 encrypted documents
        </span>
      </div>

      <div className="tax-card p-5 md:p-6 mb-6">
        {meta ? <StartReturn years={meta.years} existing={returns.map((r) => r.tax_year)} /> : <p className="text-sm text-slate-500">Loading…</p>}
        {meta && !meta.settings.intake_open && <p className="text-sm text-red-600 mt-3" data-testid="tax-intake-closed">New returns are temporarily paused.</p>}
      </div>

      <div className="space-y-3 mb-10" data-testid="tax-returns-list">
        {isLoading ? <p className="text-sm text-slate-500">Loading returns…</p> :
          returns.length === 0 ? <p className="text-sm text-slate-500" data-testid="tax-returns-empty">No returns yet — start one above.</p> :
          returns.map((r) => <ReturnRow key={r.id} r={r} onOpen={() => nav(`/tax/${r.id}`)} />)}
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-8">
        {STEPS.map((s, i) => (
          <div key={s.t} className="tax-card p-5">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-black text-red-600">0{i + 1}</span>
              <s.icon className="h-5 w-5 text-slate-700" weight="duotone" />
            </div>
            <p className="font-bold text-slate-900">{s.t}</p>
            <p className="text-sm text-slate-600 mt-1">{s.d}</p>
          </div>
        ))}
      </div>
      <Disclaimer />
    </TaxShell>
  );
}
